<template>
    <div class="my-video-container">
      <video preload="metadata" ref="videoPlayer" controls autoplay muted playsinline
        data-plyr-config='{"fullscreen": {"enabled": true, "iosNative": true}}'
        style="object-fit: contain;"></video>
    </div>
</template>

<script>
import Plyr from 'plyr';
import Hls from 'hls.js';
import 'plyr/dist/plyr.css';
import PlayerApi from '@/api/player/PlayerApi';

export default {
  data() {
    return {
      videoList: [],
      player: null,
      hls: null,
      hlsUrl: null,
      watchStartTime: null,
      totalWatchedSeconds: 0,
      heartbeatTimer: null,
      heartbeatStartTimer: null,
      watchRequestInFlight: false,
      seekCorrectionInProgress: false,
      exitSent: false,
      liveId: 2,
      userId: null,
      itemdata: null,
    };
  },
  methods: {
    blockSeekShortcut(event) {
      const blockedKeys = ["ArrowLeft", "ArrowRight", "Home", "End", "j", "J", "l", "L", "MediaRewind", "MediaFastForward"];
      const isDigitKey = event.key.length === 1 && event.key >= "0" && event.key <= "9";
      if (blockedKeys.includes(event.key) || isDigitKey) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    },
    disableSeekingControls() {
      const seekInput = this.$el.querySelector(`input[data-plyr="seek"]`);
      if (seekInput) {
        seekInput.disabled = true;
        seekInput.tabIndex = -1;
        seekInput.setAttribute("aria-disabled", "true");
      }
      this.$el.addEventListener("keydown", this.blockSeekShortcut, true);
    },
    keepAtLiveEdge() {
      const video = this.$refs.videoPlayer;
      if (!video || this.seekCorrectionInProgress || video.seekable.length === 0) return;

      const rangeIndex = video.seekable.length - 1;
      const rangeStart = video.seekable.start(rangeIndex);
      const rangeEnd = video.seekable.end(rangeIndex);
      const hlsLivePosition = this.hls && Number.isFinite(this.hls.liveSyncPosition)
        ? this.hls.liveSyncPosition
        : rangeEnd;
      const livePosition = Math.min(rangeEnd, Math.max(rangeStart, hlsLivePosition));

      if (Math.abs(video.currentTime - livePosition) < 1) return;

      this.seekCorrectionInProgress = true;
      video.currentTime = livePosition;
      window.requestAnimationFrame(() => {
        this.seekCorrectionInProgress = false;
      });
    },
    onPlay() {
      if (!this.watchStartTime) {
        this.watchStartTime = Date.now();
      }
    },
    onPauseOrExit() {
      if (this.watchStartTime) {
        const tmp = Math.floor((Date.now() - this.watchStartTime) / 1000);
        this.totalWatchedSeconds += tmp;
        this.watchStartTime = null;
      }
      this.sendWatchData();
    },
    async sendWatchData() {
        if (this.totalWatchedSeconds <= 0 || this.watchRequestInFlight) return;

        const watchedSeconds = this.totalWatchedSeconds;
        this.watchRequestInFlight = true;

        try {
            this.userId = JSON.parse(localStorage.getItem('itemData'))['userId'];
            const session_token = localStorage.getItem('info');
            const params = {
                user_id: this.userId,
                live_id: this.liveId,
                watched_seconds: watchedSeconds,
                session_token: session_token
            }
            await PlayerApi.fetchData(params);
            this.totalWatchedSeconds = Math.max(0, this.totalWatchedSeconds - watchedSeconds);
        } catch (error) {
            console.log("sendWatchData error:", error)
        } finally {
            this.watchRequestInFlight = false;
        }
    },
    // sendHeartbeatStart(){
    //   if (!document.hidden) {
    //     this.userId = JSON.parse(localStorage.getItem('itemData'))['userId'];

    //     const dataPayload = {
    //       user_id: this.userId,
    //       live_id: this.liveId,
    //       watched_seconds: 0
    //     };

    //     const blob = new Blob([JSON.stringify(dataPayload)], {
    //       type: 'application/json',
    //     });

    //     const apiBaseUrl = process.env.VUE_APP_API_PRE_URL;
    //     navigator.sendBeacon(`${apiBaseUrl}/api/player/heartbeat-start`, blob); 
    //   }
    // },
    async sendHeartbeat() {
      if (document.hidden) return;

      this.userId = JSON.parse(localStorage.getItem('itemData'))['userId'];

      if (this.watchStartTime) {
        const tmp = Math.floor((Date.now() - this.watchStartTime) / 1000);
        this.totalWatchedSeconds += tmp;
        this.watchStartTime = Date.now();
      }

      if (this.watchRequestInFlight) return;

      const watchedSeconds = this.totalWatchedSeconds;
      const dataPayload = {
        user_id: this.userId,
        live_id: this.liveId,
        session_token: localStorage.getItem('info'),
        watched_seconds: watchedSeconds,
      };

      this.watchRequestInFlight = true;
      try {
        await PlayerApi.heartbeat(dataPayload);
        this.totalWatchedSeconds = Math.max(0, this.totalWatchedSeconds - watchedSeconds);
      } catch (error) {
        console.log("sendHeartbeat error:", error);
      } finally {
        this.watchRequestInFlight = false;
      }
    },
    onUnload() {
      if (this.exitSent) return;

      if (this.watchStartTime) {
        const tmp = Math.floor((Date.now() - this.watchStartTime) / 1000);
        this.totalWatchedSeconds += tmp;
        this.watchStartTime = null;
      }

      const itemData = JSON.parse(localStorage.getItem('itemData'));
      const sessionToken = localStorage.getItem('info');
      if (!itemData || !itemData.userId || !sessionToken) return;

      this.userId = itemData.userId;
      const data = JSON.stringify({
        user_id: this.userId,
        live_id: this.liveId,
        watched_seconds: this.totalWatchedSeconds,
        session_token: sessionToken
      });

      const blob = new Blob([data], {
        type: 'application/json',
      });

      const apiBaseUrl = process.env.VUE_APP_API_PRE_URL;
      const queued = navigator.sendBeacon(`${apiBaseUrl}/api/player/exit`, blob);
      if (queued) {
        this.exitSent = true;
        this.totalWatchedSeconds = 0;
      }
    },
    onPageHide(event) {
      if (!event.persisted) {
        this.onUnload();
      }
    },
    async initSetting(tmpNumber) {
      this.hlsUrl = decodeURIComponent(tmpNumber);
    },
    async loadVideo(tmpNumber) {
        const types = { mp4: 'video/mp4', webm: 'video/webm', m3u: 'application/x-mpegURL' };

        const arr = tmpNumber.split(',');
        this.isPlaylist = arr.length > 1;

        for (let i = 0; i < arr.length; i++) {
            const add = {
            sources: [{
                src: decodeURIComponent(arr[i]),
                type: types.m3u
            }],
            };
            this.videoList.push(add);
        }

        this.player = new Plyr(this.$refs.videoPlayer, {
            debug: false,
            muted: true,
            autoplay: true,
            controls: [
              "play-large",
              "play",
              "mute",
              "volume",
              "settings",
              "pip",
              "airplay",
              "fullscreen",
            ],
            fullscreen: { enabled: true, fallback: true, iosNative: true, },
        });

        this.$refs.videoPlayer.muted = true;
        const volumeInput = document.querySelector('input[data-plyr="volume"]');
        if (volumeInput) {
            volumeInput.value = 0;
            volumeInput.dispatchEvent(new Event('input'));
        }
        this.player.source = this.videoList;

        for (let i = 0; i < this.videoList.length; i++) {
            if (this.videoList[i].sources[0].src.indexOf('m3u8') > -1) {
                this.hlsUrl = this.videoList[i].sources[0].src;
                this.currentIdx = i;
                break;
            }
        }

       
        this.player.on('ready', () => {
            console.log("player is ready");
            this.disableSeekingControls();
            
            // this.sendHeartbeatStart();
    
            this.player.on('play', this.onPlay);
            this.player.on('pause', this.onPauseOrExit);
            this.player.on('ended', this.onPauseOrExit);
            this.player.on('error', () => {
                console.log('Video error occurred.');
            });
        });
      

        if (this.hlsUrl !== null && Hls.isSupported()) {
            this.hls = new Hls();
            this.hls.loadSource(this.hlsUrl);
            this.hls.attachMedia(this.$refs.videoPlayer);
            this.hls.on(Hls.Events.MANIFEST_PARSED, () => {
                this.$refs.videoPlayer.play();
            });
        } 
        else if (this.$refs.videoPlayer.canPlayType('application/vnd.apple.mpegurl')) {
            const video = this.$refs.videoPlayer;
            video.src = this.hlsUrl;
            video.addEventListener('canplay', function () {
                video.play();
            });
        }
    },
  },
  async mounted() {
      const frontPreUrl = window.location.protocol + '//' + window.location.host;
      const tmpNumber = window.location.href.replace(frontPreUrl + '/player?', '');
      await this.initSetting(tmpNumber);
      await this.loadVideo(tmpNumber);
      this.$refs.videoPlayer.addEventListener("seeking", this.keepAtLiveEdge);

      document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
          this.onPauseOrExit();
        } else {
          if (!this.player.paused) {
            this.onPlay();
          }
        }
      });

      window.addEventListener('beforeunload', this.onUnload);
      window.addEventListener('pagehide', this.onPageHide);
      const heartbeatInitialDelay = Math.floor(Math.random() * 10000);
      this.heartbeatStartTimer = setTimeout(() => {
        this.sendHeartbeat();
        this.heartbeatTimer = setInterval(this.sendHeartbeat, 10000);   // 10초 간격으로 체크
      }, heartbeatInitialDelay);
    
  },
  beforeDestroy() {
    this.onUnload();
    this.onPauseOrExit();
    clearTimeout(this.heartbeatStartTimer);
    this.heartbeatStartTimer = null;
    clearInterval(this.heartbeatTimer);
    this.heartbeatTimer = null;
    window.removeEventListener("beforeunload", this.onUnload);
    window.removeEventListener("pagehide", this.onPageHide);
    this.$el.removeEventListener("keydown", this.blockSeekShortcut, true);
    if (this.$refs.videoPlayer) {
      this.$refs.videoPlayer.removeEventListener("seeking", this.keepAtLiveEdge);
    }

    if (this.hls) this.hls.destroy();
    if (this.player) this.player.destroy();
  },
};
</script>

<style>
.my-video-container .plyr {
  height: 100%;
  width: 100%;
}
.my-video-container {
  --plyr-color-main: var(--primary-color);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100vh;
  background-color: #000;
}
.my-video-container video {
  width: 100%;
}
.my-video-container .plyr__progress__container,
.my-video-container .plyr__progress,
.my-video-container input[data-plyr="seek"] {
  display: none !important;
}
.plyr video {
  height: 100% !important;
}
</style>
